import React, { useState } from 'react';
import { Box, Fab, Tooltip, Zoom } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// WhatsApp Icon SVG Component
const WhatsAppIcon = () => (
  <svg 
    viewBox="0 0 32 32" 
    width="28" 
    height="28" 
    fill="white"
    style={{ display: 'block' }}
  >
    <path d="M16 0C7.164 0 0 7.164 0 16c0 2.829.745 5.482 2.047 7.784L0 32l8.453-2.017A15.921 15.921 0 0016 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm0 29.333c-2.533 0-4.929-.707-6.964-1.933l-.5-.293-5.18 1.236 1.26-4.929-.32-.52A13.248 13.248 0 012.667 16c0-7.364 5.969-13.333 13.333-13.333S29.333 8.636 29.333 16 23.364 29.333 16 29.333z"/>
    <path d="M23.573 19.573c-.36-.18-2.127-1.047-2.46-1.167-.333-.12-.573-.18-.813.18-.24.36-.933 1.167-1.147 1.407-.213.24-.427.273-.787.093-.36-.18-1.52-.56-2.893-1.787-1.073-.953-1.793-2.133-2.007-2.493-.213-.36-.02-.553.16-.733.16-.16.36-.427.54-.64.18-.213.24-.36.36-.6.12-.24.06-.453-.033-.64-.093-.187-.813-1.96-1.113-2.68-.293-.7-.593-.6-.813-.613-.213-.013-.453-.013-.693-.013s-.64.093-.973.453c-.333.36-1.273 1.24-1.273 3.027s1.3 3.507 1.48 3.747c.18.24 2.547 3.893 6.173 5.46.863.373 1.533.593 2.06.76.867.273 1.653.233 2.273.14.693-.107 2.127-.867 2.427-1.707.3-.84.3-1.56.213-1.707-.087-.147-.327-.24-.687-.42z"/>
  </svg>
);

// Pulse animation
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(37, 211, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0);
  }
`;

// Hover scale animation
const hoverScale = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

// Styled floating button
const StyledFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  right: '24px',
  top: '50%',
  transform: 'translateY(-50%)',
  width: '60px',
  height: '60px',
  backgroundColor: '#25D366',
  color: 'white',
  boxShadow: '0 4px 12px rgba(37, 211, 102, 0.4)',
  zIndex: 1000,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: `${pulse} 2s infinite`,
  
  '&:hover': {
    backgroundColor: '#20BA5A',
    transform: 'translateY(-50%) translateX(-3px)',
    boxShadow: '0 8px 20px rgba(37, 211, 102, 0.5)',
    animation: `${hoverScale} 0.6s ease-in-out`,
  },
  
  '&:active': {
    transform: 'translateY(-50%) translateX(-1px)',
    boxShadow: '0 4px 12px rgba(37, 211, 102, 0.4)',
  },

  // 响应式设计
  [theme.breakpoints.down('md')]: {
    right: '16px',
    width: '56px',
    height: '56px',
  },
  
  // 移动端显示在右下角（避免与返回顶部按钮重叠）
  [theme.breakpoints.down('sm')]: {
    top: 'auto',
    bottom: '100px',
    right: '16px',
    transform: 'none',
    width: '48px',
    height: '48px',
    
    '&:hover': {
      transform: 'translateY(-3px)',
    },
    
    '&:active': {
      transform: 'translateY(-1px)',
    },
  },
}));

// Tooltip content container
const TooltipContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  
  '& .tooltip-title': {
    fontSize: '15px',
    fontWeight: 600,
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    letterSpacing: '0.3px',
  },
  
  '& .tooltip-subtitle': {
    fontSize: '12px',
    fontWeight: 400,
    color: 'rgba(255, 255, 255, 0.85)',
    lineHeight: 1.4,
    marginTop: '2px',
  },
  
  '& .tooltip-icon': {
    fontSize: '18px',
    animation: `${hoverScale} 1.5s ease-in-out infinite`,
  },
});

const WhatsAppFloat = ({ phoneNumber = '6589208197' }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // 点击处理函数
  const handleClick = () => {
    // 移除电话号码中的所有非数字字符
    const cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Open WhatsApp conversation
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent('Hello! I would like to inquire about Chinese courses.')}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Zoom in={true} timeout={500}>
      <Tooltip
        title={
          <TooltipContent>
            <div className="tooltip-title">
              <span className="tooltip-icon">💬</span>
              <span>Contact Us</span>
            </div>
            <div className="tooltip-subtitle">
              Click to inquire about courses
            </div>
          </TooltipContent>
        }
        placement="left"
        arrow
        open={showTooltip}
        onOpen={() => setShowTooltip(true)}
        onClose={() => setShowTooltip(false)}
        TransitionComponent={Zoom}
        componentsProps={{
          tooltip: {
            sx: {
              background: 'linear-gradient(135deg, #07284b 0%, #1e40af 100%)',
              fontSize: '14px',
              padding: '14px 18px',
              borderRadius: '12px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25), 0 2px 8px rgba(7, 40, 75, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              maxWidth: '220px',
              '& .MuiTooltip-arrow': {
                color: '#07284b',
                '&::before': {
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                },
              },
            },
          },
        }}
      >
        <StyledFab
          onClick={handleClick}
          aria-label="Contact us on WhatsApp"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <WhatsAppIcon />
        </StyledFab>
      </Tooltip>
    </Zoom>
  );
};

export default WhatsAppFloat;

